import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

// Feed-row shape — mirrors GET /api/admin/deals (research/phase-3.md § 10 §4.1).
// All deal fields string|null; irr/yoc already re-aliased server-side FROM
// targetIrr/targetYoc (research/phase-3.md § 11 risk #2).
export interface AdminDealRow {
  id: string; // conversation id (row key + detail-fetch param)
  title: string | null; // deal title
  ownerUserId: string; // for the ?owner= filter
  ownerName: string; // batch-resolved (fallback 'an Arcanum user')
  pipelineStatus: string | null; // Status column (6-value pipeline vocab)
  market: string | null;
  units: string | null;
  basis: string | null;
  irr: string | null; // ← server-aliased FROM targetIrr
  yoc: string | null; // ← server-aliased FROM targetYoc
  hasPublicShare: boolean;
  publicShareId: string | null; // null ⇒ INTERNAL chip; non-null ⇒ PUBLIC SHARE chip
}

export interface AdminDealsResponse {
  conversations: AdminDealRow[];
  total: number;
  hasMore: boolean;
}

// api.get does NOT unwrap the {success,data} envelope (api.ts:334-335) — declare
// <{data:...}> + read .data (research/phase-3.md § 11 risk #4, §4 envelope row).
interface AdminDealsEnvelope {
  data: AdminDealsResponse;
}

export interface AdminDealsFilters {
  owner?: string; // owner user_id
  status?: string; // 6-value pipeline status
  limit?: number; // server: min(1).max(100).default(20)
  offset?: number; // server: min(0).default(0)
}

export function useAdminDeals(filters: AdminDealsFilters = {}) {
  const { owner, status, limit, offset } = filters;
  return useQuery<AdminDealsResponse>({
    queryKey: ['admin', 'deals', { owner, status, limit, offset }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (owner) params.set('owner', owner);
      if (status) params.set('status', status);
      if (limit != null) params.set('limit', String(limit));
      if (offset != null) params.set('offset', String(offset));
      const qs = params.toString();
      const envelope = await api.get<AdminDealsEnvelope>(
        `/api/admin/deals${qs ? `?${qs}` : ''}`
      );
      return envelope.data;
    },
    staleTime: 30_000,
    // refetchOnWindowFocus not set — handled centrally by
    // <FocusResumeCoordinator> (mirrors use-admin-status.ts:52-53).
  });
}
